import { useMemo, useEffect, useState } from "react";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { Formik, FormikHelpers } from "formik";
import { BEModulesClassNames } from "@utils/moduleList";
import { ModuleFull } from "./storageTypes";
import { useContextAwareGetStorage, useUpdateStoragesMutation, useUpdateStorageEntryMutation, useContextAwareGetStorageEntries } from "@hooks/index";
import { getSchemas, validationSchema, initialValues, refrigerantNullData } from "./storage";
import { FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import StorageTierOne from "./StorageTierOne";
import GenericTierTwo from "../GenericTierTwo";

const Storages = () => {
  const { activeActivityId } = useAppSelector(selectCurrentProject);
  const { moduleList } = useAppSelector(selectCurrentBuilder);
  const id = moduleList.find((module) => module.id === FEModules.Storage)?.uniqueId;

  const { data: storagesData, isLoading, isError, error } = useContextAwareGetStorage(id ?? 0, { refetchOnMountOrArgChange: true });
  const { data: entriesData, isLoading: isEntriesLoading, isError: isEntriesError, error: entriesError } = useContextAwareGetStorageEntries({ activityId: activeActivityId ?? 0 });
  const [updateStorages, { isError: isUpdateError, error: updateError }] = useUpdateStoragesMutation();
  const [updateStorageEntry, { isError: isUpdateStorageEntryError, error: updateStorageEntryError, isLoading: isUpdateStorageEntryLoading }] = useUpdateStorageEntryMutation();

  const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
  const [initValues, setInitValues] = useState<ModuleFull>(initialValues);
  const { errorMsg: updateParentErrorMsg } = useModuleHook({
    skeletonsLoadingDeps: [isLoading, isEntriesLoading],
    isMutateError: isUpdateError,
    mutateError: updateError,
  });

  const { errorMsg: fetchError } = useErrorMessage({ isError: isEntriesError ?? isError, error: entriesError ?? error });
  const { errorMsg: updateEntryErrorMsg } = useErrorMessage({ isError: isUpdateStorageEntryError, error: updateStorageEntryError });
  const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

  useEffect(() => {
    if (!storagesData) return
    setInitValues((cur) => ({ ...cur, parent: storagesData }))
  }, [storagesData])

  useEffect(() => {
    if (entriesData)
      processModuleThreads({ storages: entriesData })
  }, [processModuleThreads, entriesData])

  const handleSubmit = async (
    values: ModuleFull,
    { resetForm }: FormikHelpers<ModuleFull>
  ) => {
    const { storages, parent } = values
    try {
      const promises: Promise<any>[] = []
      const parentPayload = { body: { ...parent }, activityId: activeActivityId ?? 0 }

      storages.map((entry) => {
        const payload = { body: entry.is_refrigerant_used ? { ...entry } : { ...entry, ...refrigerantNullData }, activityId: activeActivityId ?? 0 }
        if (entry.id) promises.push(updateStorageEntry(payload))
        return Promise.resolve()
      })

      await Promise.all(promises);
      await new Promise(resolve => setTimeout(resolve, 500));
      if (parent.id) updateStorages(parentPayload)
      resetForm({ values })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Formik
      initialValues={initValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      <>
        <StorageTierOne
          title="project.storage"
          initValues={initValues}
          t2Schema={t2Schema}
          beModuleType={BEModulesClassNames.Storage}
          moduleSchema={moduleSchema}
          isLoading={isLoading || isEntriesLoading || isUpdateStorageEntryLoading}
          isEntriesLoading={isEntriesLoading}
          footerError={updateParentErrorMsg ?? updateEntryErrorMsg}
          blockingError={fetchError}
          setInitValues={setInitValues}
        />
        <GenericTierTwo
          title="project.storage"
          t2Schema={t2Schema}
        />
      </>
    </Formik>
  );
};

export default Storages;

